import React, { useEffect, useState, useRef } from 'react';
import {
    Container,
    Box,
    Typography,
    Button,
    TextField,
    CircularProgress,
    Snackbar,
    Alert,
    Paper,
    Grid,
    useTheme,
    useMediaQuery,
    Fade,
    Divider,
    InputAdornment,
    CardActions,
    CardContent
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import SearchIcon from '@mui/icons-material/Search';
import QuizIcon from '@mui/icons-material/Quiz';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SchoolIcon from '@mui/icons-material/School';
import { useLanguage } from './LanguageContext';

export default function TakeTest() {
    const { t } = useLanguage();
    const [quizId, setQuizId] = useState('');
    const [quizDetails, setQuizDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
    const navigate = useNavigate();
    const detailsRef = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        document.title = `${t.takeTest} | ClassCapture`;
    }, [t]);

    const handleSearch = async () => {
        if (!quizId) {
            setSnackbar({ open: true, message: t.enterQuizIdError, severity: 'error' });
            return;
        }
        setQuizDetails(null);
        setLoading(true);
        try {
            const auth = getAuth();
            const token = await auth.currentUser.getIdToken();
            const url = `${process.env.REACT_APP_BACKEND_URL}/quizzes/search/${quizId}`;
            const response = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (!response.ok) {
                if (response.status === 404) {
                    throw new Error(t.quizNotFoundError);
                }
            }

            const data = await response.json();
            setQuizDetails(data);
        } catch (error) {
            setSnackbar({
                open: true,
                message: error.message,
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (quizDetails && detailsRef.current) {
            detailsRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [quizDetails]);

    const handleCloseSnackbar = (_, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar(s => ({ ...s, open: false }));
    };

    const gradientBackground = {
        background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`
    };

    if (loading && !quizDetails) {
        return (
            <Container sx={{
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                height: '100vh', flexDirection: 'column', gap: 1
            }}>
                <SchoolIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                <Typography variant="subtitle1" color="primary" fontWeight={450}>
                    ClassCapture
                </Typography>
                <CircularProgress color="primary" size={24} />
            </Container>
        );
    }

    return (
        <Container component="main" maxWidth="lg" sx={{ py: 2 }}>
            {/* Header */}
            <Fade in timeout={800}>
                <Paper elevation={1} sx={{
                    p: 2, mb: 2, borderRadius: 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    ...gradientBackground, color: 'white'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <QuizIcon sx={{ fontSize: 24, mr: 1, color:"black" }} />
                        <Typography variant="h6" fontWeight={450} color={"black"}>
                            {t.takeTest}
                        </Typography>
                    </Box>
                    <TextField
                        placeholder={t.searchQuizIdPlaceholder}
                        variant="outlined"
                        size="small"
                        value={quizId}
                        onChange={e => setQuizId(e.target.value)}
                        sx={{
                            width: isMobile ? '100px' : '180px',
                            backgroundColor: 'rgba(255, 255, 255, 0.15)',
                            borderRadius: 1,
                            '& .MuiOutlinedInput-root fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                            '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                            '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: 'white' },
                            '& input': { color: 'white', padding: '6px 10px', fontSize: '0.875rem' }
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <SearchIcon
                                        sx={{ color: 'white', fontSize: 18, cursor: 'pointer' }}
                                        onClick={handleSearch}
                                    />
                                </InputAdornment>
                            )
                        }}
                        onKeyDown={e => e.key === 'Enter' && handleSearch()}
                    />
                </Paper>
            </Fade>

            {/* Instructions */}
            {!quizDetails && (
                <Fade in timeout={1000}>
                    <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 1, backgroundColor: 'background.paper' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <InfoOutlinedIcon sx={{ fontSize: 16, mr: 1, color: theme.palette.secondary.main }} />
                            <Typography variant="subtitle1" fontWeight={450}>
                                {t.howToFindQuizId}
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {t.howToFindQuizIdDesc1}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {t.howToFindQuizIdDesc2}
                        </Typography>
                    </Paper>
                </Fade>
            )}

            {/* Quiz Details */}
            {quizDetails && (
                <Fade in timeout={1000}>
                    <Box ref={detailsRef}>
                        <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                            <Typography variant="subtitle1" fontWeight={450}>
                                {t.quizDetails}
                            </Typography>
                            <Divider sx={{ ml: 2, flexGrow: 1 }} />
                        </Box>
                        <Paper elevation={1} sx={{
                            display: 'flex', flexDirection: 'column', borderRadius: 1,
                            overflow: 'hidden', transition: 'transform 0.15s, box-shadow 0.15s',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: 2 }
                        }}>
                            <Box sx={{ p: 1, ...gradientBackground, color: 'black', display: 'flex', alignItems: 'center' }}>
                                <QuizIcon sx={{ mr: 1, fontSize: 16 }} />
                                <Typography variant="body2" fontWeight={450} noWrap>
                                    {quizDetails.title}
                                </Typography>
                            </Box>
                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary">
                                            {t.createdBy}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={450}>
                                            {quizDetails.createdBy}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary">
                                            {t.participantsLabel}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={450}>
                                            {quizDetails.takenBy}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary">
                                            {t.timeLimitLabel}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={450}>
                                            {quizDetails.timeLimit} {t.minutes}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary">
                                            {t.numQuestionsLabel}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={450}>
                                            {quizDetails.numberOfQuestions}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                            <Divider />
                            <CardActions sx={{ p: 0 }}>
                                <Button
                                    fullWidth
                                    onClick={() => navigate(`/attempt/${quizId}`)}
                                    endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                                    sx={{
                                        p: 0.75, textTransform: 'none', fontWeight: 500,
                                        justifyContent: 'space-between', fontSize: '0.75rem'
                                    }}
                                >
                                    {t.takeThisTest}
                                </Button>
                            </CardActions>
                        </Paper>
                    </Box>
                </Fade>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}